/**
 * Функция для корректного подбора числительного слова
 * @param {number} number - Число для которого нужно подобрать числительное
 * @param {Array} words - Массив из трех слов в формате ['час', 'часа', 'часов'] или ['минута', 'минуты', 'минут']
 * @returns {string} - Корректное числительное слово
 *
 * Примеры использования:
 * getNumeral(1, ['час', 'часа', 'часов']) // 'час'
 * getNumeral(2, ['час', 'часа', 'часов']) // 'часа'
 * getNumeral(5, ['час', 'часа', 'часов']) // 'часов'
 * getNumeral(21, ['час', 'часа', 'часов']) // 'час'
 * getNumeral(22, ['час', 'часа', 'часов']) // 'часа'
 * getNumeral(25, ['час', 'часа', 'часов']) // 'часов'
 */
export const getNumeral = (number, words) => {
	// Проверяем входные параметры
	if (!Array.isArray(words) || words.length !== 3) {
		console.error('getNumeral: words должен быть массивом из 3 элементов');
		return words[0] || '';
	}

	if (typeof number !== 'number' || isNaN(number)) {
		console.error('getNumeral: number должен быть числом');
		return words[0] || '';
	}

	const [one, few, many] = words;

	// Получаем последнюю цифру и предпоследнюю
	const lastDigit = number % 10;
	const lastTwoDigits = number % 100;

	// Исключения для чисел 11-14
	if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
		return many;
	}

	// Для остальных случаев смотрим на последнюю цифру
	switch (lastDigit) {
		case 1:
			return one;
		case 2:
		case 3:
		case 4:
			return few;
		case 0:
		case 5:
		case 6:
		case 7:
		case 8:
		case 9:
			return many;
		default:
			return many;
	}
};

/**
 * Функция для форматирования времени из минут в часы с округлением в большую сторону
 * @param {number} totalMinutes - Общее количество минут
 * @returns {string} - Отформатированное время в формате "X ч."
 *
 * Примеры:
 * formatTimeHourWithNumerals(60) // '1 ч.'
 * formatTimeHourWithNumerals(30) // '0.5 ч.'
 * formatTimeHourWithNumerals(1) // '0.02 ч.' (округление в большую сторону)
 * formatTimeHourWithNumerals(0.6) // '0.01 ч.' (округление в большую сторону)
 * formatTimeHourWithNumerals(120) // '2 ч.'
 */
export const formatTimeHourWithNumerals = (totalMinutes) => {
	if (!totalMinutes || totalMinutes <= 0) return '0 ч.';

	// Конвертируем минуты в часы
	const totalHours = totalMinutes / 60;

	// Округляем в большую сторону до сотых
	const roundedHours = Math.ceil(totalHours * 100) / 100;

	// Форматируем до 2 знаков после запятой, убираем лишние нули
	const formattedHours = roundedHours % 1 === 0
		? roundedHours.toString()
		: roundedHours.toFixed(2).replace(/\.?0+$/, '');

	return `${formattedHours} ч.`;
};

/**
 * Функция для форматирования количества с корректными числительными
 * @param {number} count - Количество
 * @param {Array} words - Массив из трех слов
 * @returns {string} - Отформатированная строка с числом и корректным числительным
 *
 * Примеры:
 * formatCount(1, ['заказ', 'заказа', 'заказов']) // '1 заказ'
 * formatCount(5, ['заказ', 'заказа', 'заказов']) // '5 заказов'
 * formatCount(21, ['заказ', 'заказа', 'заказов']) // '21 заказ'
 */
export const formatCount = (count, words) => {
	if (typeof count !== 'number' || isNaN(count)) {
		return `0 ${words[2] || ''}`;
	}

	return `${count} ${getNumeral(count, words)}`;
};

export default {
	getNumeral,
	formatTimeHourWithNumerals,
	formatCount
};
